import { useMemo } from "react";
import { find, map, reject } from "lodash";
import { getFacetName } from "@linkfluence/i18n";
import { Facet } from "@linkfluence/models/facet";
import { Locale } from "@linkfluence/models/locale";
import { Metric } from "@linkfluence/models/metric";
import { evolution, evolutionPercentagePoint, percent } from "@tootools/number";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { DatavizHorizontalBarChart } from "@radarly/models/dataviz";
import {
  GeoTerm,
  SearchFragmentGeo,
  SearchFragmentGeoPlace,
  getTermFromAggs,
} from "@radarly/models/search";

interface HookProps {
  data: SearchFragmentGeo["data"];
  comparaisonData: SearchFragmentGeo["data"];
  fragment: SearchFragmentGeo;
  dataviz: DatavizHorizontalBarChart;
}

function getPreviousPlace(places: SearchFragmentGeoPlace[], key: string) {
  return find(places, {
    key,
  });
}

function getGaugeFromPlace(
  place: SearchFragmentGeoPlace,
  comparaisonPlaces: SearchFragmentGeoPlace[],
  metric: Metric,
  mainUnit: DatavizHorizontalBarChart["mainUnit"],
  total: number,
  previousTotal: number,
  locale: Locale
) {
  const count = place.counts[metric];
  const evolution = getEvolutionFromPlace(
    place,
    comparaisonPlaces,
    metric,
    mainUnit,
    total,
    previousTotal
  );
  if (place.location) {
    return {
      id: place.key,
      count,
      label: place.location.name,
      evolution,
    };
  }
  return {
    id: place.key,
    count,
    label: getFacetName(locale, Facet.Countries, place.key),
    evolution,
  };
}

function getEvolutionFromPlace(
  place: SearchFragmentGeoPlace,
  comparaisonPlaces: SearchFragmentGeoPlace[],
  metric: Metric,
  mainUnit: DatavizHorizontalBarChart["mainUnit"],
  total: number,
  previousTotal: number
) {
  const findPreviousPlace = getPreviousPlace(comparaisonPlaces, place.key);
  const previousCount = getCountFromPlace(findPreviousPlace, metric);
  if (!previousCount) {
    return null;
  }

  const count = getCountFromPlace(place, metric);

  if (mainUnit === "percentage") {
    const before = percent(previousCount, previousTotal);
    const after = percent(count, total);
    return evolutionPercentagePoint(before, after);
  } else {
    return evolution(previousCount, count);
  }
}

function getCountFromPlace(place: SearchFragmentGeoPlace, metric: Metric) {
  return place ? place.counts[metric] : null;
}

function getTotalFromData(data: SearchFragmentGeo["data"], metric: Metric) {
  if (!data) {
    return null;
  }
  return data.aggs && data.aggs.metrics
    ? data.aggs.metrics.metrics[metric]
    : null;
}

function getPlacesFromData(data: SearchFragmentGeo["data"], field: GeoTerm) {
  return data && data.aggs && data.aggs[field] ? data.aggs[field].values : [];
}

function formatGauges(
  data: SearchFragmentGeo["data"],
  comparaisonData: SearchFragmentGeo["data"],
  field: GeoTerm,
  metric: Metric,
  locale: Locale,
  mainUnit: DatavizHorizontalBarChart["mainUnit"]
) {
  const places = getPlacesFromData(data, field);
  const comparaisonPlaces = getPlacesFromData(comparaisonData, field);
  const currentTotalCount = getTotalFromData(data, metric);
  const previousTotalCount = getTotalFromData(comparaisonData, metric);
  return map(reject(places, { key: "xx" }), (place) => {
    return getGaugeFromPlace(
      place,
      comparaisonPlaces,
      metric,
      mainUnit,
      currentTotalCount,
      previousTotalCount,
      locale
    );
  });
}

function getPenetrationRateTotal(
  data: SearchFragmentGeo["data"],
  metric: Metric
) {
  return data && data.aggs.metrics ? data.aggs.metrics.metrics[metric] : null;
}

export function useHorizontalBarChart({
  fragment,
  dataviz,
  data,
  comparaisonData,
}: HookProps) {
  const locale = useCurrentUserLocale();
  const field = getTermFromAggs(fragment.request.aggs) as GeoTerm;
  const metric = fragment.request.aggs[field].metrics[0];
  const gauges = useMemo(() => {
    return formatGauges(
      data,
      comparaisonData,
      field,
      metric,
      locale,
      dataviz.mainUnit
    );
  }, [data, comparaisonData, field, metric, locale, dataviz.mainUnit]);
  const total = getPenetrationRateTotal(data, metric);
  return {
    total,
    gauges,
  };
}
